// SCR-STAFF-889 — گزارش‌های مالی پایه (US-ACC-060): گردش و مانده صندوق، بانک و PSP؛ درآمد به تفکیک دسته و شعبه؛ مانده‌های معوق (کیف پول،
// بیعانه، بسته)؛ مطالبات و بدهی‌ها — بر پایه ردیف‌های منبع ثبت‌شده و نقش حساب (C-AGENT-631).
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type AccBalanceRow } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  EmptyState,
  ErrorState,
  JalaliDatePickerRHF,
  ListPage,
  LoadingState,
  toBusinessDate,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { accKeys, Amount, SCR_STAFF_889, useAccBranches, useBranchName } from './common';
import { roleKey } from './MappingsPage';

function monthStart(): string {
  const today = toBusinessDate(new Date()) ?? '';
  return today.length >= 8 ? `${today.slice(0, 8)}01` : today;
}

export function ReportsPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const { control, watch } = useForm<{ from: string | null; to: string | null }>({
    defaultValues: { from: monthStart(), to: toBusinessDate(new Date()) },
  });
  const from = watch('from') ?? monthStart();
  const to = watch('to') ?? from;
  const [branchId, setBranchId] = useState('');
  const report = useQuery({
    queryKey: accKeys.report(session?.tenantId, `${from}|${to}|${branchId}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/basic', {
          params: { query: { from, to, branchId: branchId || undefined } },
        }),
      ),
  });
  // US-ACC-064: گزارش مالیاتی همان بازه و شعبه (AC-ACC-064.1).
  const tax = useQuery({
    queryKey: [...accKeys.report(session?.tenantId, `${from}|${to}|${branchId}`), 'tax'],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/tax', {
          params: { query: { from, to, branchId: branchId || undefined } },
        }),
      ),
  });
  const label = (row: { role: string; label: string }) =>
    row.label || t(roleKey(row.role), { defaultValue: row.role });
  const data = report.data;
  const empty =
    data &&
    [data.cashAndBank, data.revenue, data.deferred, data.receivables, data.liabilities].every(
      (rows) => rows.length === 0,
    );
  return (
    <ListPage
      screenId={SCR_STAFF_889}
      titleKey="acc:report.title"
      subtitleKey="acc:report.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="acc:common.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="acc:common.to" />
          <TextField
            select
            size="small"
            label={t('acc:common.branch')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
    >
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {empty ? <EmptyState titleKey="acc:report.empty" /> : null}
      {tax.data ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="acc-report-tax">
          <Typography variant="subtitle1" gutterBottom>
            {t('acc:tax.title')}
          </Typography>
          <Table size="small" aria-label={t('acc:tax.title')}>
            <TableBody>
              <TableRow>
                <TableCell>{t('acc:tax.taxable_sales')}</TableCell>
                <TableCell align="right" data-testid="acc-tax-taxable-sales">
                  <Amount money={tax.data.taxableSales} />
                </TableCell>
                <TableCell>{t('acc:tax.sales_tax')}</TableCell>
                <TableCell align="right" data-testid="acc-tax-sales-tax">
                  <Amount money={tax.data.salesTax} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('acc:tax.taxable_purchases')}</TableCell>
                <TableCell align="right" data-testid="acc-tax-taxable-purchases">
                  <Amount money={tax.data.taxablePurchases} />
                </TableCell>
                <TableCell>{t('acc:tax.purchase_credit')}</TableCell>
                <TableCell align="right" data-testid="acc-tax-purchase-credit">
                  <Amount money={tax.data.purchaseCredit} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('acc:tax.net_payable')}</TableCell>
                <TableCell align="right" data-testid="acc-tax-net-payable">
                  <Amount money={tax.data.netPayable} />
                </TableCell>
                <TableCell>{t('acc:tax.withholding_total')}</TableCell>
                <TableCell align="right" data-testid="acc-tax-withholding-total">
                  <Amount money={tax.data.withholdingTotal} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {t('acc:tax.withholding')}
          </Typography>
          {tax.data.withholding.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="acc-tax-withholding-empty">
              {t('acc:tax.withholding_empty')}
            </Typography>
          ) : (
            <Table size="small" aria-label={t('acc:tax.withholding')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('acc:tax.party')}</TableCell>
                  <TableCell>{t('acc:tax.party_type')}</TableCell>
                  <TableCell align="right">{t('acc:tax.amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tax.data.withholding.map((row) => (
                  <TableRow key={`${row.partyType ?? 'none'}-${row.partyRef ?? 'none'}`}>
                    <TableCell>
                      <bdi dir="ltr">{row.partyRef ?? '—'}</bdi>
                    </TableCell>
                    <TableCell>{row.partyType ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Amount money={row.amount} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      ) : null}
      {data && !empty ? (
        <Stack spacing={2}>
          <BalanceTable
            title={t('acc:report.cash_and_bank')}
            rows={data.cashAndBank}
            label={label}
            testId="acc-report-cash"
          />
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="acc-report-revenue">
            <Typography variant="subtitle1" gutterBottom>
              {t('acc:report.revenue')}
            </Typography>
            <Table size="small" aria-label={t('acc:report.revenue')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('acc:report.item')}</TableCell>
                  <TableCell>{t('acc:common.branch')}</TableCell>
                  <TableCell align="right">{t('acc:report.amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.revenue.map((row) => (
                  <TableRow key={`${row.role}-${row.parameter ?? ''}-${row.branchId}`}>
                    <TableCell>{label(row)}</TableCell>
                    <TableCell>{branchName(row.branchId)}</TableCell>
                    <TableCell align="right">
                      <Amount money={row.amount} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <BalanceTable
            title={t('acc:report.deferred')}
            rows={data.deferred}
            label={label}
            testId="acc-report-deferred"
          />
          <BalanceTable
            title={t('acc:report.receivables')}
            rows={data.receivables}
            label={label}
            testId="acc-report-receivables"
          />
          <BalanceTable
            title={t('acc:report.liabilities')}
            rows={data.liabilities}
            label={label}
            testId="acc-report-liabilities"
          />
        </Stack>
      ) : null}
    </ListPage>
  );
}

function BalanceTable({
  title,
  rows,
  label,
  testId,
}: {
  title: string;
  rows: readonly AccBalanceRow[];
  label: (row: AccBalanceRow) => string;
  testId: string;
}) {
  const { t } = useTranslation('acc');
  if (rows.length === 0) {
    return null;
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={testId}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            <TableCell>{t('acc:report.item')}</TableCell>
            <TableCell align="right">{t('acc:report.opening')}</TableCell>
            <TableCell align="right">{t('acc:report.debit')}</TableCell>
            <TableCell align="right">{t('acc:report.credit')}</TableCell>
            <TableCell align="right">{t('acc:report.closing')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key} data-testid="acc-report-row" data-role={row.role}>
              <TableCell>{label(row)}</TableCell>
              <TableCell align="right">
                <Amount money={row.opening} />
              </TableCell>
              <TableCell align="right">
                <Amount money={row.debit} />
              </TableCell>
              <TableCell align="right">
                <Amount money={row.credit} />
              </TableCell>
              <TableCell align="right">
                <Amount money={row.closing} testId="acc-report-closing" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
